import React, { useState, useRef, useEffect } from 'react';
import {
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
  getAdditionalUserInfo,
} from 'firebase/auth';
import type { MultiFactorInfo, MultiFactorResolver, PhoneMultiFactorInfo, User } from 'firebase/auth';
import { auth } from '../firebase';
import type { LastProvider } from '../storage/savedProfile';

type HandleUserOpts = {
  remember?: boolean;
  provider?: LastProvider;
  profile?: { displayName?: string | null; photoURL?: string | null };
};

interface Props {
  resolver: MultiFactorResolver;
  remember: boolean;
  provider: LastProvider;
  onUser: (user: User, opts?: HandleUserOpts) => Promise<void>;
  onError: (message: string) => void;
  onSignInFailure: (message: string) => void;
  onBack: () => void;
}

function Spinner() {
  return (
    <div className="h-4 w-4 animate-spin rounded-full border-2 border-sf-blue-dark border-t-transparent" />
  );
}

function isPhoneHint(h: MultiFactorInfo): h is PhoneMultiFactorInfo {
  return h.factorId === PhoneMultiFactorGenerator.FACTOR_ID;
}

export function MfaPanel({ resolver, remember, provider, onUser, onError, onSignInFailure, onBack }: Props) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [smsState, setSmsState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);

  // Tear the reCAPTCHA widget down ourselves before unmount — otherwise its
  // async DOM mutations race React's own teardown of recaptchaRef and throw
  // "Failed to execute 'insertBefore' on 'Node'" from inside react-dom.
  useEffect(() => {
    return () => {
      try {
        verifierRef.current?.clear();
      } catch {
        /* noop */
      }
      verifierRef.current = null;
    };
  }, []);

  // Prefer TOTP over phone if both enrolled
  const totpHint = resolver.hints.find(
    (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
  const phoneHint = resolver.hints.find(isPhoneHint);
  const mode: 'totp' | 'phone' | 'unknown' = totpHint
    ? 'totp'
    : phoneHint
    ? 'phone'
    : 'unknown';

  async function sendSms() {
    if (!phoneHint || !isPhoneHint(phoneHint)) return;
    if (smsState !== 'idle') return; // guard double-fire (e.g. double click)
    setSmsState('sending');
    try {
      // Clear any stale widget before minting a new one on the same node —
      // reusing the container without clearing leaves two grecaptcha
      // instances fighting over it, which is what produced the insertBefore
      // crash and the 400 from identitytoolkit.
      verifierRef.current?.clear();
      const verifier = new RecaptchaVerifier(auth, recaptchaRef.current!, {
        size: 'invisible',
      });
      verifierRef.current = verifier;
      const provider = new PhoneAuthProvider(auth);
      const vId = await provider.verifyPhoneNumber(
        { multiFactorHint: phoneHint, session: resolver.session },
        verifier,
      );
      setVerificationId(vId);
      setSmsState('sent');
    } catch (err: unknown) {
      setSmsState('idle');
      onError(err instanceof Error ? err.message : 'Failed to send SMS code.');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      let assertion;

      if (mode === 'totp' && totpHint) {
        assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim());
      } else if (mode === 'phone' && verificationId) {
        const credential = PhoneAuthProvider.credential(verificationId, code.trim());
        assertion = PhoneMultiFactorGenerator.assertion(credential);
      } else {
        onError('MFA not ready — please try again.');
        setLoading(false);
        return;
      }

      const result = await resolver.resolveSignIn(assertion);
      const info = getAdditionalUserInfo(result);
      const p = info?.profile as { name?: string; picture?: string } | undefined;
      await onUser(result.user, {
        remember,
        provider,
        profile: { displayName: p?.name ?? null, photoURL: p?.picture ?? null },
      });
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      const map: Record<string, string> = {
        'auth/invalid-verification-code': 'Incorrect code. Try again.',
        'auth/missing-verification-code': 'Incorrect code. Try again.',
        'auth/invalid-credential': 'Incorrect code. Try again.',
        'auth/totp-verification-failed': 'Incorrect code. Try again.',
        'auth/invalid-totp-code': 'Incorrect code. Try again.',
        'auth/code-expired': 'Code expired. Request a new one.',
        'auth/missing-verification-id': 'Code expired. Request a new one.',
        'auth/too-many-requests': 'Too many attempts. Try again later.',
        'auth/network-request-failed': 'Network error. Check your connection.',
      };
      const msg = map[code] ?? (err instanceof Error ? err.message : 'Invalid code. Please try again.');
      onSignInFailure(msg);
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = code.length >= 6 && (mode === 'totp' || Boolean(verificationId));

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sf-blue-vibrant/20">
          <svg
            className="h-6 w-6 text-sf-blue-vibrant"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
            />
          </svg>
        </div>
        <h2 className="font-khand-semibold text-2xl text-gray-800 dark:text-white">Two-step verification</h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {mode === 'totp' && 'Enter the 6-digit code from your authenticator app'}
          {mode === 'phone' && phoneHint && (
            <>
              Verify via SMS to{' '}
              <span className="text-gray-800 dark:text-white">
                {(phoneHint as PhoneMultiFactorInfo).phoneNumber}
              </span>
            </>
          )}
          {mode === 'unknown' && 'No supported MFA factor detected'}
        </p>
      </div>

      {/* Hidden reCAPTCHA mount point for phone MFA */}
      <div ref={recaptchaRef} />

      {mode === 'unknown' && (
        <div className="rounded-xl bg-red-500/10 px-4 py-3 text-center text-sm text-red-500 dark:text-red-400">
          No supported MFA method found on this account. Contact your administrator.
        </div>
      )}

      {mode !== 'unknown' && (
        <div className="flex items-center gap-2.5 rounded-xl bg-sf-blue-vibrant/10 px-3.5 py-2.5 text-xs text-gray-500 dark:text-gray-400">
          <img src="/dataflow.ico" alt="" className="h-4 w-4 shrink-0" />
          <span>
            This is the same code used to sign in to{' '}
            <a
              href="https://dataflow.sellersflow.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-sf-blue-vibrant hover:underline"
            >
              DataFlow
            </a>
            .
          </span>
        </div>
      )}

      {/* Phone: send SMS button before code entry */}
      {mode === 'phone' && smsState === 'idle' && (
        <button
          onClick={sendSms}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-sf-blue-vibrant py-3 font-medium text-sf-blue-dark transition hover:bg-sf-blue-vibrant-dark"
        >
          Send SMS code
        </button>
      )}

      {mode === 'phone' && smsState === 'sending' && (
        <div className="flex items-center justify-center gap-2 py-2 text-sm text-gray-400">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-sf-blue-vibrant border-t-transparent" />
          Sending SMS…
        </div>
      )}

      {/* Code input (TOTP always, phone after SMS sent) */}
      {(mode === 'totp' || smsState === 'sent') && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            type="text"
            inputMode="numeric"
            placeholder="000000"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-center text-2xl tracking-[0.5em] text-sf-blue-dark placeholder-gray-400 outline-none focus:border-sf-blue-vibrant dark:border-white/20 dark:bg-white/10 dark:text-white dark:placeholder-gray-600"
          />
          <button
            type="submit"
            disabled={!canSubmit || loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-sf-blue-vibrant py-3 font-medium text-sf-blue-dark transition hover:bg-sf-blue-vibrant-dark disabled:opacity-60"
          >
            {loading && <Spinner />}
            {loading ? 'Verifying…' : 'Verify'}
          </button>
        </form>
      )}

      <button
        onClick={onBack}
        className="text-center text-sm text-gray-500 transition hover:text-gray-300"
      >
        ← Back to sign in
      </button>
    </div>
  );
}
